// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';

// material ui
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';

// shared components
import { SubmitButton } from '@/components/ui/button/SubmitButton';

// feature-components
import EnergyXplorerAlarmManagementActions from '@/features/energyxplorer/components/alarms/EnergyXplorerAlarmManagementActions';

type EnergyXplorerAlarmSetupCardProps = {
  accountId: string | undefined;
  title: string;
  onEnable: () => Promise<void>;
  isLoading: boolean;
  autoCreatedAlarmGroupName?: string | null;
  canDeleteAutoCreatedAlarmGroup?: boolean;
  isDeletingAutoCreatedAlarmGroup?: boolean;
  onDeleteAutoCreatedAlarmGroup?: () => Promise<boolean>;
};

const EnergyXplorerAlarmSetupCard = ({
  accountId,
  title,
  onEnable,
  isLoading,
  autoCreatedAlarmGroupName,
  canDeleteAutoCreatedAlarmGroup = false,
  isDeletingAutoCreatedAlarmGroup = false,
  onDeleteAutoCreatedAlarmGroup,
}: EnergyXplorerAlarmSetupCardProps) => {
  const { t } = useTranslation('energy-analysis');

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
        <Stack spacing={2.5}>
          {/* Header */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" gap={1}>
              <NotificationsActiveRoundedIcon color="primary" />

              <Typography variant="h6">{title}</Typography>
            </Box>

            <EnergyXplorerAlarmManagementActions
              accountId={accountId}
              autoCreatedAlarmGroupName={autoCreatedAlarmGroupName}
              canDeleteAutoCreatedAlarmGroup={canDeleteAutoCreatedAlarmGroup}
              isDeletingAutoCreatedAlarmGroup={isDeletingAutoCreatedAlarmGroup}
              onDeleteAutoCreatedAlarmGroup={onDeleteAutoCreatedAlarmGroup}
            />
          </Box>

          {/* Setup state */}
          <Box
            sx={(theme) => ({
              p: 2,
              borderRadius: 2,
              bgcolor: theme.palette.action.hover,
            })}
          >
            <Stack spacing={1.5}>
              <Box display="flex" gap={1.25} alignItems="flex-start">
                <InfoOutlinedIcon
                  color="primary"
                  fontSize="small"
                  sx={{ mt: 0.25 }}
                />

                <Box>
                  <Typography variant="subtitle2">
                    {t('alarm_setup.title')}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.25 }}
                  >
                    {t('alarm_setup.description')}
                  </Typography>
                </Box>
              </Box>

              <Box>
                <SubmitButton loading={isLoading} onClick={onEnable}>
                  {t('alarm_setup.action')}
                </SubmitButton>
              </Box>
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default EnergyXplorerAlarmSetupCard;
